import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import * as $3Dmol from '3dmol';

// Jmol element colors (hex strings) — matches 3Dmol's Jmol colorscheme
const JMOL_COLORS = {
  H:'#FFFFFF',He:'#D9FFFF',Li:'#CC80FF',Be:'#C2FF00',B:'#FFB5B5',C:'#909090',N:'#3050F8',O:'#FF0D0D',
  F:'#90E050',Ne:'#B3E3F5',Na:'#AB5CF2',Mg:'#8AFF00',Al:'#BFA6A6',Si:'#F0C8A0',P:'#FF8000',S:'#FFFF30',
  Cl:'#1FF01F',Ar:'#80D1E3',K:'#8F40D4',Ca:'#3DFF00',Sc:'#E6E6E6',Ti:'#BFC2C7',V:'#A6A6AB',Cr:'#8A99C7',
  Mn:'#9C7AC7',Fe:'#E06633',Co:'#F090A0',Ni:'#50D050',Cu:'#C88033',Zn:'#7D80B0',Ga:'#C28F8F',Ge:'#668F8F',
  As:'#BD80E3',Se:'#FFA100',Br:'#A62929',Kr:'#5CB8D1',Rb:'#702EB0',Sr:'#00FF00',Y:'#94FFFF',Zr:'#94E0E0',
  Nb:'#73C2C9',Mo:'#54B5B5',Tc:'#3B9E9E',Ru:'#248F8F',Rh:'#0A7D8C',Pd:'#006985',Ag:'#C0C0C0',Cd:'#FFD98F',
  In:'#A67573',Sn:'#668080',Sb:'#9E63B5',Te:'#D47A00',I:'#940094',Xe:'#429EB0',Cs:'#57178F',Ba:'#00C900',
  La:'#70D4FF',Ce:'#FFFFC7',Pr:'#D9FFC7',Nd:'#C7FFC7',Pm:'#A3FFC7',Sm:'#8FFFC7',Eu:'#61FFC7',Gd:'#45FFC7',
  Tb:'#30FFC7',Dy:'#1FFFC7',Ho:'#00FF9C',Er:'#00E675',Tm:'#00D452',Yb:'#00BF38',Lu:'#00AB24',Hf:'#4DC2FF',
  Ta:'#4DA6FF',W:'#2194D6',Re:'#267DAB',Os:'#266696',Ir:'#175487',Pt:'#D0D0E0',Au:'#FFD123',Hg:'#B8B8D0',
  Tl:'#A6544D',Pb:'#575961',Bi:'#9E4FB5',Th:'#00BAFF',U:'#008FFF',
};

// Covalent radii (Å) — comprehensive table including metals/lanthanides that 3Dmol misses
const COVALENT_R = {
  H:0.37,He:0.32,Li:1.34,Be:0.90,B:0.82,C:0.77,N:0.75,O:0.73,F:0.71,Ne:0.69,
  Na:1.54,Mg:1.30,Al:1.18,Si:1.11,P:1.06,S:1.02,Cl:0.99,Ar:0.97,
  K:1.96,Ca:1.74,Sc:1.44,Ti:1.56,V:1.25,Cr:1.27,Mn:1.39,Fe:1.25,Co:1.26,Ni:1.21,
  Cu:1.38,Zn:1.31,Ga:1.26,Ge:1.22,As:1.19,Se:1.16,Br:1.14,Kr:1.10,
  Rb:2.11,Sr:1.92,Y:1.62,Zr:1.48,Nb:1.37,Mo:1.45,Tc:1.56,Ru:1.26,Rh:1.35,Pd:1.31,
  Ag:1.53,Cd:1.48,In:1.44,Sn:1.41,Sb:1.38,Te:1.35,I:1.33,Xe:1.30,
  Cs:2.25,Ba:1.98,La:1.69,Ce:1.65,Pr:1.65,Nd:1.64,Pm:1.63,Sm:1.62,Eu:1.85,Gd:1.61,
  Tb:1.59,Dy:1.59,Ho:1.58,Er:1.57,Tm:1.56,Yb:1.74,Lu:1.56,
  Hf:1.50,Ta:1.38,W:1.46,Re:1.59,Os:1.44,Ir:1.37,Pt:1.28,Au:1.44,Hg:1.49,
  Tl:1.48,Pb:1.47,Bi:1.46,Th:1.79,U:1.70,
};

// Max coordination number per element — prevents over-bonding in metallic/ionic systems
const MAX_COORD = {
  H:1, He:0, F:1, Cl:4, Br:4, I:4, Ne:0, Ar:0, Kr:0, Xe:0,
  O:3, N:4, C:4, B:4, S:6, P:6, Se:6, Te:6, Si:6, Ge:6,
  // Transition metals: typically 6-8 in crystals
  Sc:8, Ti:8, V:8, Cr:8, Mn:8, Fe:8, Co:8, Ni:8, Cu:8, Zn:8,
  Y:9, Zr:9, Nb:8, Mo:8, Ru:8, Rh:8, Pd:8, Ag:8, Cd:8,
  Hf:9, Ta:8, W:8, Re:8, Os:8, Ir:8, Pt:8, Au:8, Hg:8,
  // Alkali/alkaline earth: high CN in ionic crystals
  Li:6, Na:8, K:12, Rb:12, Cs:12, Be:6, Mg:8, Ca:12, Sr:12, Ba:12,
  // Lanthanides
  La:12, Ce:12, Pr:12, Nd:12, Sm:12, Eu:12, Gd:12, Tb:12, Dy:12, Ho:12, Er:12, Tm:12, Yb:12, Lu:12,
  // Post-transition metals / metalloids
  Al:6, Ga:6, In:8, Tl:8, Sn:8, Pb:8, Bi:8, Sb:6, As:6,
  Th:12, U:12,
};

function getUniqueElements(frame) {
  const seen = new Set();
  const elems = [];
  if (!frame.atomLines) return elems;
  for (const al of frame.atomLines) {
    const m = (al.trim().split(/\s+/)[0] || '').match(/^[A-Z][a-z]?/);
    if (m && !seen.has(m[0])) { seen.add(m[0]); elems.push(m[0]); }
  }
  return elems;
}

function parseAtomLine(line) {
  const parts = line.trim().split(/\s+/);
  const elem = (parts[0] || '').match(/^[A-Z][a-z]?/)?.[0] || 'X';
  return { elem, x: parseFloat(parts[1]), y: parseFloat(parts[2]), z: parseFloat(parts[3]) };
}

// Build supercell by replicating atoms along lattice vectors
function buildSupercellXyz(frame, nx, ny, nz) {
  const lat = frame.lattice_matrix;
  if (!lat || lat.length < 3 || (nx === 1 && ny === 1 && nz === 1)) {
    // No lattice info or 1x1x1 — return original
    const lines = [String(frame.n), frame.comment || ''];
    for (const al of frame.atomLines) lines.push(al);
    return { xyz: lines.join('\n'), count: frame.n };
  }
  const a = lat[0], b = lat[1], c = lat[2];
  const newLines = [];
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) {
      for (let k = 0; k < nz; k++) {
        const tx = i * a[0] + j * b[0] + k * c[0];
        const ty = i * a[1] + j * b[1] + k * c[1];
        const tz = i * a[2] + j * b[2] + k * c[2];
        for (const al of frame.atomLines) {
          const p = parseAtomLine(al);
          newLines.push(`${p.elem}  ${(p.x + tx).toFixed(6)}  ${(p.y + ty).toFixed(6)}  ${(p.z + tz).toFixed(6)}`);
        }
      }
    }
  }
  const totalAtoms = newLines.length;
  const comment = frame.comment || '';
  return { xyz: [String(totalAtoms), comment, ...newLines].join('\n'), count: totalAtoms };
}

// Crystal-friendly bond detection with coordination number cap.
// Finds all potential bonds within covalent radii + tolerance, then keeps
// only the shortest bonds per atom up to MAX_COORD to prevent over-bonding
// in metallic/ionic systems (e.g., Cs with 2.25 Å radius).
function assignCrystalBonds(atoms) {
  const FUDGE = 0.45;
  const MIN_DIST_SQ = 0.16; // 0.4² — ignore overlapping atoms

  for (let i = 0; i < atoms.length; i++) {
    if (atoms[i].index == null) atoms[i].index = i;
    atoms[i].bonds = [];
    atoms[i].bondOrder = [];
  }

  // Collect all candidate bonds with distances
  const candidateBonds = [];
  for (let i = 0; i < atoms.length; i++) {
    const a = atoms[i];
    const ra = COVALENT_R[a.elem] || 1.6;
    for (let j = i + 1; j < atoms.length; j++) {
      const b = atoms[j];
      const rb = COVALENT_R[b.elem] || 1.6;
      const maxDist = ra + rb + FUDGE;
      const maxSq = maxDist * maxDist;
      const dx = a.x - b.x; if (dx * dx > maxSq) continue;
      const dy = a.y - b.y; if (dy * dy > maxSq) continue;
      const dz = a.z - b.z;
      const dSq = dx * dx + dy * dy + dz * dz;
      if (dSq > MIN_DIST_SQ && dSq <= maxSq) {
        candidateBonds.push({ i, j, dSq });
      }
    }
  }

  // Sort by distance — shortest bonds are most likely real
  candidateBonds.sort((a, b) => a.dSq - b.dSq);

  // Track bond count per atom
  const bondCount = new Array(atoms.length).fill(0);

  // Assign bonds, respecting max coordination
  for (const { i, j } of candidateBonds) {
    const a = atoms[i], b = atoms[j];
    const maxA = MAX_COORD[a.elem] || 12;
    const maxB = MAX_COORD[b.elem] || 12;
    if (bondCount[i] >= maxA || bondCount[j] >= maxB) continue;
    a.bonds.push(b.index);
    a.bondOrder.push(1);
    b.bonds.push(a.index);
    b.bondOrder.push(1);
    bondCount[i]++;
    bondCount[j]++;
  }
}

const SUPERCELL_OPTIONS = [
  { label: '1×1×1', nx: 1, ny: 1, nz: 1 },
  { label: '2×2×2', nx: 2, ny: 2, nz: 2 },
  { label: '3×3×3', nx: 3, ny: 3, nz: 3 },
  { label: '2×2×1', nx: 2, ny: 2, nz: 1 },
  { label: '2×1×1', nx: 2, ny: 1, nz: 1 },
];

export default function CrystalViewer3D({ frame, cifText, style, customBonds = false, captureRef }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const [spinning, setSpinning] = useState(true);
  const [supercell, setSupercell] = useState(0); // index into SUPERCELL_OPTIONS

  const hasLattice = frame?.lattice_matrix?.length === 3;
  const sc = SUPERCELL_OPTIONS[supercell] || SUPERCELL_OPTIONS[0];

  // Memoize supercell expansion
  const expandedData = useMemo(() => {
    if (!frame?.atomLines?.length) return null;
    return buildSupercellXyz(frame, sc.nx, sc.ny, sc.nz);
  }, [frame, sc.nx, sc.ny, sc.nz]);

  useEffect(() => {
    if (!containerRef.current || !frame) return;

    const viewer = $3Dmol.createViewer(containerRef.current, {
      backgroundColor: '#0f1923',
      antialias: true,
    });
    viewerRef.current = viewer;
    if (captureRef) captureRef.current = () => { try { return viewer.pngURI(2); } catch (_) { return null; } };

    const hasXyz = expandedData != null;
    if (hasXyz) {
      if (customBonds) {
        const model = viewer.addModel(expandedData.xyz, 'xyz', { assignBonds: false });
        assignCrystalBonds(model.selectedAtoms({}));
      } else {
        viewer.addModel(expandedData.xyz, 'xyz');
      }
    } else if (typeof cifText === 'string' && cifText.trim()) {
      // M11: guard against non-string cif values (a stripped-run placeholder once
      // fabricated `cif: true`) — addModel(true,'cif') throws inside this effect
      // and the root ErrorBoundary replaced the whole app.
      viewer.addModel(cifText, 'cif', { doAssembly: false, duplicateAssemblyAtoms: false });
    }

    viewer.setStyle({}, {
      sphere: { scale: 0.35, colorscheme: 'Jmol' },
      stick: { radius: 0.12, colorscheme: 'Jmol' },
    });
    viewer.zoomTo();
    viewer.render();
    viewer.spin('y', 0.5);
    setSpinning(true);

    return () => {
      try {
        viewer.spin(false);
        viewer.clear();
      } catch (_) {}
      viewerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frame, cifText, expandedData]);

  const toggleSpin = useCallback(() => {
    setSpinning(prev => {
      const next = !prev;
      if (viewerRef.current) {
        if (next) viewerRef.current.spin('y', 0.5);
        else viewerRef.current.spin(false);
      }
      return next;
    });
  }, []);

  const elements = frame ? getUniqueElements(frame) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* 3D viewport */}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: 450,
          position: 'relative',
          borderRadius: 8,
          overflow: 'hidden',
          ...style,
        }}
      />
      {/* Controls bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        {/* Element legend */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {elements.map(el => (
            <span key={el} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              <span style={{
                display: 'inline-block', width: 14, height: 14, borderRadius: '50%',
                backgroundColor: JMOL_COLORS[el] || '#888',
                border: '1px solid rgba(255,255,255,0.3)',
                flexShrink: 0,
              }} />
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{el}</span>
            </span>
          ))}
        </div>
        {/* Right-side controls */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Supercell selector — only shown when lattice vectors are available */}
          {hasLattice && (
            <select
              className="form-select form-select-sm bg-dark text-light"
              style={{ width: 110, fontSize: 12 }}
              value={supercell}
              onChange={(e) => setSupercell(Number(e.target.value))}
            >
              {SUPERCELL_OPTIONS.map((opt, i) => (
                <option key={opt.label} value={i}>{opt.label}</option>
              ))}
            </select>
          )}
          {/* Auto-rotate toggle */}
          <button
            className={`btn btn-sm ${spinning ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={toggleSpin}
            style={{ minWidth: 110 }}
          >
            {spinning ? 'Stop Rotation' : 'Auto-Rotate'}
          </button>
        </div>
      </div>
    </div>
  );
}
